import {
  Actions,
  type FormTypesKey,
  InventoryListFormFields,
  type InventoryListFormState,
} from '@openx/types/inventoryList.ts';
import { type ChangeEvent, useCallback, useReducer } from 'react';
import { useFormContext } from 'react-hook-form';
import { useValidate } from '../../../hooks/useValidate.ts';
import { addBulkItems } from './utils/addBulkItems.ts';
import { dispatchValidate } from './utils/dispatchers.ts';
import { filterAndMapItems } from './utils/filterAndMapItems.ts';
import { getInitialData } from './utils/getInitialData.ts';
import { reducerInventoryList } from './utils/reducerInventoryList.ts';
import { validateBulkInput } from './utils/validateBulkInput.ts';

export const useHandleBulkState = (type: FormTypesKey) => {
  const { getValues, setValue } = useFormContext<InventoryListFormState>();
  const [state, dispatch] = useReducer(reducerInventoryList, getInitialData(getValues(InventoryListFormFields.VALUES)));
  const validateInput = useValidate(type);

  const handleBulkValidation = useCallback(() => {
    const validationResult = validateBulkInput(state.bulkInput, state.listData, validateInput);

    dispatchValidate(dispatch, validationResult);
  }, [state.bulkInput, state.listData, validateInput]);

  const handleBulkAdd = useCallback(() => {
    const updatedValue = addBulkItems(state.validItems, getValues(InventoryListFormFields.VALUES), dispatch);

    setValue(InventoryListFormFields.VALUES, updatedValue, { shouldDirty: true, shouldValidate: true });
  }, [getValues, setValue, state.validItems]);

  const handleRemoveItem = useCallback(
    (id: string) => {
      const { filteredListData, filteredNames } = filterAndMapItems(state.listData, id);

      setValue(InventoryListFormFields.VALUES, filteredNames, { shouldDirty: true, shouldValidate: true });
      dispatch({ type: Actions.SET_LIST_DATA, payload: filteredListData });
    },
    [setValue, state.listData]
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch({ type: Actions.SET_BULK_INPUT, payload: e.target.value });
  }, []);

  const handleRemoveAll = useCallback(() => {
    setValue(InventoryListFormFields.VALUES, [], { shouldDirty: true, shouldValidate: true });
    dispatch({ type: Actions.SET_LIST_DATA, payload: [] });
  }, [setValue]);

  const handleBulkReplace = useCallback(
    (items: Array<string>) => {
      dispatch({ type: Actions.SET_BULK_INPUT, payload: items.join(`\n`) });

      const validationResult = validateBulkInput(items.join(`\n`), state.listData, validateInput);

      dispatchValidate(dispatch, validationResult);
    },
    [state.listData, validateInput]
  );

  return {
    state,
    dispatch,
    handleBulkValidation,
    handleBulkAdd,
    handleRemoveItem,
    handleInputChange,
    handleRemoveAll,
    handleBulkReplace,
  };
};
